import { useEffect, useMemo, useRef } from "react"
import { boundsIntersect } from "@nova/calculate-geospatial"
import { useDebouncedViewport } from "features/map/GlobalViewState"
import { useLocalStorageValue } from "tools/store/localStorageRegistry"
import {
    getMinNotamShapeArea,
    getPaddedViewportBounds,
    isAreaVisible,
    isCategoryVisible,
} from "../../zoomLod"
import { useAirspaceLayer } from "../../useAirspaceLayer"
import { NOTAM_LAYER_ID } from "../../layerIds"
import { fetchAndCacheNotams, useNotamCache } from "./notamCache"

const VIEWPORT_UPDATE_DEBOUNCE_MS = 300

export const useNotamLayer = (enabled: boolean) => {
    const abortRef = useRef<AbortController | null>(null)
    const getNewAbortController = () => {
        abortRef.current?.abort()
        const abortController = new AbortController()
        abortRef.current = abortController
        return abortController
    }

    const airspaceFilters = useLocalStorageValue("airspaceFilters")
    const notamsCategoryVisible = airspaceFilters.categoriesVisible.notams
    const notamTypesVisible = airspaceFilters.notams

    const viewport = useDebouncedViewport(VIEWPORT_UPDATE_DEBOUNCE_MS)

    useEffect(() => {
        if (!notamsCategoryVisible) return
        const abortController = getNewAbortController()
        fetchAndCacheNotams({ abortController })
        return () => {
            abortRef.current?.abort()
            abortRef.current = null
        }
    }, [notamsCategoryVisible, viewport])

    const cache = useNotamCache()
    const minShapeArea = getMinNotamShapeArea(viewport.zoom)
    const paddedViewportBounds = useMemo(() => getPaddedViewportBounds(viewport), [viewport])
    const visibleFeatures = useMemo(
        () =>
            notamsCategoryVisible
                ? [...cache.values()]
                      .filter((cached) =>
                          isCategoryVisible(cached.properties.notamType, notamTypesVisible)
                      )
                      .filter((cached) => isAreaVisible(cached.area, minShapeArea))
                      .filter((cached) => boundsIntersect(cached.bbox, paddedViewportBounds))
                      .map((cached) => cached.feature)
                : [],
        [notamsCategoryVisible, cache, notamTypesVisible, minShapeArea, paddedViewportBounds]
    )

    return useAirspaceLayer({ type: NOTAM_LAYER_ID, visibleFeatures, enabled })
}
